import type {
  MRT_ColumnPinningState,
  MRT_ColumnSizingState,
  MRT_SortingState,
} from 'material-react-table';
import type {
  DensitySetting,
  ProviderSource,
  ReportFiltersState,
} from '../pages/reports/viewState';
import { normalizeSavedViewConfig } from '../pages/reports/viewState';

/**
 * Complete view state for a specific provider
 */
export interface ProviderViewState {
  providerSource: ProviderSource;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columnSizing: MRT_ColumnSizingState;
  columnPinning: MRT_ColumnPinningState;
  filterState: ReportFiltersState;
  sorting: MRT_SortingState;
  density: DensitySetting;
  compactHeaders: boolean;
  zebraRows: boolean;
  pageSize: number;
  lastModified: string; // ISO timestamp
}

/**
 * Save complete view state for a specific provider/tenant combination
 */
export function saveProviderViewState(
  provider: ProviderSource,
  tenantSlug: string,
  state: Omit<ProviderViewState, 'providerSource' | 'lastModified'>,
): void {
  try {
    const key = `afl_view_${tenantSlug}_${provider}`;
    const stateToSave: ProviderViewState = {
      ...state,
      providerSource: provider,
      lastModified: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(stateToSave));
  } catch (error) {
    console.error('[ProviderStorage] Failed to save provider state:', error);
  }
}

/**
 * Load saved view state for a specific provider/tenant combination
 */
export function loadProviderViewState(
  provider: ProviderSource,
  tenantSlug: string,
): ProviderViewState | null {
  try {
    const key = `afl_view_${tenantSlug}_${provider}`;
    const saved = localStorage.getItem(key);
    if (!saved) {
      return null;
    }

    const parsed = JSON.parse(saved);

    // Validate structure
    if (!parsed || typeof parsed !== 'object') {
      console.warn('[ProviderStorage] Invalid state structure, removing');
      localStorage.removeItem(key);
      return null;
    }

    // Normalize to ensure type safety
    const normalized = normalizeSavedViewConfig(parsed);
    if (!normalized) {
      console.warn('[ProviderStorage] State failed normalization, removing');
      localStorage.removeItem(key);
      return null;
    }

    // Return as ProviderViewState
    return {
      providerSource: parsed.providerSource || provider,
      columnVisibility: normalized.columnVisibility || {},
      columnOrder: normalized.columnOrder || [],
      columnSizing: normalized.columnSizing || {},
      columnPinning: normalized.columnPinning || { left: [], right: [] },
      filterState: {
        typeCodeFilter: normalized.typeCodeFilter || [],
        lineTypeFilter: normalized.lineTypeFilter || [],
        linePayFilter: normalized.linePayFilter || [],
        coveredLineFilter: normalized.coveredLineFilter || [],
        columnFilters: normalized.columnFilters || [],
      },
      sorting: normalized.sorting || [],
      density: normalized.density || 'compact',
      compactHeaders: normalized.compactHeaders ?? true,
      zebraRows: normalized.zebraRows ?? true,
      pageSize: normalized.pageSize || 50,
      lastModified: parsed.lastModified || new Date().toISOString(),
    };
  } catch (error) {
    console.error('[ProviderStorage] Failed to load provider state:', error);
    return null;
  }
}

/**
 * Clear saved state for a specific provider/tenant
 */
export function clearProviderViewState(provider: ProviderSource, tenantSlug: string): void {
  const key = `afl_view_${tenantSlug}_${provider}`;
  localStorage.removeItem(key);
}

/**
 * Clear all provider states for a tenant
 */
export function clearAllProviderStates(tenantSlug: string): void {
  // const providers: ProviderSource[] = ['opentrack', 'dealer_vault', 'both'];
  // providers.forEach((provider) => {
  //   clearProviderViewState(provider, tenantSlug);
  // });
  const providers: ProviderSource[] = ['opentrack'];
  providers.forEach((provider) => {
    clearProviderViewState(provider, tenantSlug);
  });
}

/**
 * Check if a provider has saved state
 */
export function hasProviderViewState(provider: ProviderSource, tenantSlug: string): boolean {
  const key = `afl_view_${tenantSlug}_${provider}`;
  return localStorage.getItem(key) !== null;
}

/**
 * Get metadata about all saved provider states for a tenant
 */
export function getProviderStateMetadata(tenantSlug: string): Array<{
  provider: ProviderSource;
  lastModified: string;
  exists: boolean;
}> {
  // const providers: ProviderSource[] = ['opentrack', 'dealer_vault', 'both'];
  // return providers.map((provider) => {
  const providers: ProviderSource[] = ['opentrack'];
  return providers.map((provider) => {
    const state = loadProviderViewState(provider, tenantSlug);
    return {
      provider,
      lastModified: state?.lastModified || '',
      exists: state !== null,
    };
  });
}
